import React, { useEffect, useState } from "react";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useAlert } from "../alerts/AlertContext";
import AxiosInstance from "../../services/AxiosInstance";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { ASSET_WAREHOUSE_URL } from "../../Constants/Constants";


const WarehouseForm = ({
    open,
    onClose,
    editData = null,
    onSaved
}) => {

    const showAlert = useAlert();

    const [saving, setSaving] =
        useState(false);

    const [formData, setFormData] =
        useState({
            code: "",
            name: "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            postalCode: "",
            country: "India",           
            enabled: true,
            description: ""
        });


    useEffect(() => {

        if (editData) {

            setFormData({
                code: editData.code ?? "",
                name: editData.name ?? "",
                addressLine1:
                    editData.addressLine1 ?? "",
                addressLine2:
                    editData.addressLine2 ?? "",
                city: editData.city ?? "",
                state: editData.state ?? "",
                postalCode:
                    editData.postalCode ?? "",
                country:
                    editData.country ?? "India",                
                enabled:
                    editData.enabled ?? true,
                description:
                    editData.description ?? ""
            });

        } else {

            setFormData({
                code: "",
                name: "",
                addressLine1: "",
                addressLine2: "",
                city: "",
                state: "",
                postalCode: "",
                country: "India",               
                enabled: true,
                description: ""
            });
        }

    }, [editData, open]);


    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    const handleEnabledChange = (event) => {

        setFormData(prev => ({
            ...prev,
            enabled:
                event.target.value === "true"
        }));
    };


    const validate = () => {

        if (!formData.code.trim()) {
            showAlert(
                "Warehouse code is required",
                "error"
            );
            return false;
        }

        if (!formData.name.trim()) {
            showAlert(
                "Warehouse name is required",
                "error"
            );
            return false;
        }

        if (!formData.addressLine1.trim()) {
            showAlert(
                "Address Line 1 is required",
                "error"
            );
            return false;
        }

        if (!formData.city.trim()) {
            showAlert(
                "City is required",
                "error"
            );
            return false;
        }

        if (!formData.country.trim()) {
            showAlert(
                "Country is required",
                "error"
            );
            return false;
        }

        return true;
    };


    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        try {

            setSaving(true);

            const payload = {
                code: formData.code.trim(),
                name: formData.name.trim(),
                addressLine1:
                    formData.addressLine1.trim(),
                addressLine2:
                    formData.addressLine2.trim(),
                city: formData.city.trim(),
                state: formData.state.trim(),
                postalCode:
                    formData.postalCode.trim(),
                country:
                    formData.country.trim(),                
                enabled: formData.enabled,
                description:
                    formData.description.trim()
            };

            let response;

            if (editData?.id) {

                response = await AxiosInstance.put(
                    `${ASSET_WAREHOUSE_URL}/${editData.id}`,
                    payload
                );

            } else {

                response = await AxiosInstance.post(
                    ASSET_WAREHOUSE_URL,
                    payload
                );
            }

            showAlert(
                editData
                    ? "Warehouse updated successfully"
                    : "Warehouse created successfully",
                "success"
            );

            onSaved?.(response.data);

            onClose();

        } catch (error) {

            console.error(error);

            showAlert(
                error?.response?.data?.message ||
                "Unable to save warehouse",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
            fullWidth
            maxWidth="md"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        "space-between"
                }}
            >

                {editData
                    ? "Edit Warehouse"
                    : "Add Warehouse"}

                <IconButton
                    onClick={onClose}
                    disabled={saving}
                >
                    <CloseIcon />
                </IconButton>

            </DialogTitle>


            <DialogContent dividers>

                <ThemeProvider>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr"
                            },
                            gap: 2,
                            pt: 1
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Warehouse Code"
                            name="code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                        />

                        <TextField
                            fullWidth
                            label="Warehouse Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />


                        <TextField
                            fullWidth
                            label="Address Line 1"
                            name="addressLine1"
                            value={
                                formData.addressLine1
                            }
                            onChange={handleChange}
                            required
                        />

                        <TextField
                            fullWidth
                            label="Address Line 2"
                            name="addressLine2"
                            value={
                                formData.addressLine2
                            }
                            onChange={handleChange}
                        />


                        <TextField
                            fullWidth
                            label="City"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            required
                        />

                        <TextField
                            fullWidth
                            label="State"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                        />

                        <TextField
                            fullWidth
                            label="Postal Code"
                            name="postalCode"
                            value={
                                formData.postalCode
                            }
                            onChange={handleChange}
                        />

                        <TextField
                            fullWidth
                            label="Country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            required
                        />                       

                        <FormControl>

                            <FormLabel>
                                Enabled
                            </FormLabel>

                            <RadioGroup
                                row
                                value={
                                    formData.enabled
                                        ? "true"
                                        : "false"
                                }
                                onChange={
                                    handleEnabledChange
                                }
                            >

                                <FormControlLabel
                                    value="true"
                                    control={
                                        <Radio />
                                    }
                                    label="Yes"
                                />

                                <FormControlLabel
                                    value="false"
                                    control={
                                        <Radio />
                                    }
                                    label="No"
                                />

                            </RadioGroup>

                        </FormControl>


                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            name="description"
                            value={
                                formData.description
                            }
                            onChange={handleChange}
                            sx={{
                                gridColumn: {
                                    xs: "span 1",
                                    md: "span 2"
                                }
                            }}
                        />

                    </Box>

                </ThemeProvider>

            </DialogContent>


            <DialogActions
                sx={{
                    px: 3,
                    py: 2
                }}
            >

                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving
                        ? "Saving..."
                        : editData
                            ? "Update"
                            : "Save"}
                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default WarehouseForm;